import { memo, useRef, useEffect, useState, useCallback } from "react";
import { parseISO, startOfDay, endOfDay, getTime } from "date-fns";
import type { TimeEntry } from "@/types/time";
import { formatDuration, formatTime12h } from "@/lib/time-utils";

interface TimesheetCellProps {
  entries: TimeEntry[];
  date: string;
  projectColors: Map<string, string>;
}

interface Block {
  x: number;
  width: number;
  color: string;
  entry: TimeEntry;
}

const BAR_HEIGHT = 20;
const DEFAULT_COLOR = "#94A3B8";

export const TimesheetCell = memo(function TimesheetCell({ entries, date, projectColors }: TimesheetCellProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(100);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    entry: TimeEntry;
  } | null>(null);
  const blocksRef = useRef<Block[]>([]);

  const dateObj = parseISO(date);
  const dayStart = getTime(startOfDay(dateObj));
  const dayEnd = getTime(endOfDay(dateObj));
  const dayMs = dayEnd - dayStart;

  const totalSeconds = entries.reduce((sum, e) => sum + (e.durationSeconds ?? 0), 0);

  const buildBlocks = useCallback(
    (width: number): Block[] => {
      return entries.map((entry) => {
        const start = Math.max(getTime(parseISO(entry.start)), dayStart);
        const end = Math.min(getTime(parseISO(entry.end)), dayEnd);
        const x = ((start - dayStart) / dayMs) * width;
        const blockWidth = Math.max(((end - start) / dayMs) * width, 2);
        const color = entry.projectId
          ? projectColors.get(entry.projectId) ?? DEFAULT_COLOR
          : DEFAULT_COLOR;
        return { x, width: blockWidth, color, entry };
      });
    },
    [entries, dayStart, dayEnd, dayMs, projectColors]
  );

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, width: number) => {
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, width * dpr, BAR_HEIGHT * dpr);
      ctx.scale(dpr, dpr);

      // Background
      ctx.fillStyle = "rgba(128, 128, 128, 0.06)";
      ctx.beginPath();
      ctx.roundRect(0, 0, width, BAR_HEIGHT, 3);
      ctx.fill();

      // Blocks
      const blocks = buildBlocks(width);
      blocksRef.current = blocks;
      for (const block of blocks) {
        ctx.fillStyle = block.color;
        ctx.beginPath();
        ctx.roundRect(block.x, 2, block.width, BAR_HEIGHT - 4, 2);
        ctx.fill();
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
    },
    [buildBlocks]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      setCanvasWidth(entries[0].contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = BAR_HEIGHT * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${BAR_HEIGHT}px`;

    draw(ctx, canvasWidth);
  }, [canvasWidth, draw]);

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const hit = blocksRef.current.find((b) => x >= b.x && x <= b.x + b.width);
    if (hit) {
      setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, entry: hit.entry });
    } else {
      setTooltip(null);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <canvas
        ref={canvasRef}
        className="w-full cursor-crosshair"
        style={{ height: BAR_HEIGHT }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      />
      <p className="mt-1 text-center text-xs text-muted-foreground">
        {formatDuration(totalSeconds)}
      </p>
      {tooltip && (
        <div
          className="pointer-events-none absolute z-50 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
          style={{
            left: Math.min(tooltip.x, canvasWidth - 160),
            top: tooltip.entry.project || tooltip.entry.task ? -68 : -50,
          }}
        >
          <p className="text-sm font-medium">{formatDuration(tooltip.entry.durationSeconds)}</p>
          {(tooltip.entry.project || tooltip.entry.task) && (
            <p className="text-muted-foreground">
              {tooltip.entry.project?.name}
              {tooltip.entry.task && ` · ${tooltip.entry.task.name}`}
            </p>
          )}
          <p className="text-muted-foreground">
            {tooltip.entry.description ?? "No description"}
          </p>
          <p className="text-muted-foreground">
            {formatTime12h(tooltip.entry.start)} – {formatTime12h(tooltip.entry.end)}
          </p>
        </div>
      )}
    </div>
  );
});
